import { observer, Observer } from "mobx-react-lite";
import {
    Input,
    Col,
    Row,
    Select,
    DatePicker,
    Upload,
    Form,
    UploadProps,
    Skeleton,
    Button,
    InputNumber,
    TimePicker,
    Tooltip,
    Tag,
} from "antd";
import { TaskCreateVisualStore } from "../stores";
import Dragger from "antd/lib/upload/Dragger";
import React from "react";
import { RcFile } from "antd/lib/upload";
import { FULL_DATE, ONLY_DATE, Utils } from "../../common/misc/Utils";
import TaskTemplateVisualStore from "../../task_templates/stores/TaskTemplatesVisualStore";
import { TaskTemplate } from "../../task_templates/types";
import { TaskTypeDueDate } from "../../task_types/types";
import { FormInstance } from "antd/es/form";
import {
    InfoCircleOutlined,
    MinusCircleOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import { UserProfile } from "../../common/services/types";
import dayjs, { Dayjs } from "dayjs";
import { Editor } from "@progress/kendo-react-editor";
import { AdvancedToolbar } from "../../common/components/EditorToolbar";
import { useCreatedTaskNotfication } from "../../common/components/hooks/useCreatedTaskNotfication";
import TaskCreateFormAttachment from "./TaskCreateFormAttachment";
import MultiValueMetadataInput from "./MultiValueMetadataInput";
import {
    getSelectableTaskTypeId,
    getSelectableTaskTypes,
} from "../../task_types/helpers/taskTypeSelection";
const FormItem = Form.Item;

type Props = {
    store: TaskCreateVisualStore;
    templateStore: TaskTemplateVisualStore;
    form: FormInstance;
};

const TaskCreateForm: React.FC<Props> = ({ store, templateStore, form }) => {
    const [isSharedWithVisible, setIsSharedWithVisible] = React.useState(false);
    const [isTemplateApplied, setIsTemplateApplied] = React.useState(false);
    const [isNameOverriden, setIsNameOverriden] = React.useState(false);
    const [descriptionValue, setDescriptionValue] = React.useState("");
    const projectDataLoadRef = React.useRef<{
        projectId: string;
        promise: Promise<unknown>;
    }>();

    const notifySuccess = useCreatedTaskNotfication();

    React.useEffect(() => {
        form.setFieldsValue({ projectId: store.currentProjectId });
        loadProjectData(store.currentProjectId!);
    }, []);

    React.useEffect(() => {
        if (!store.newTaskDialogVisible) {
            form.resetFields();
            setIsSharedWithVisible(false);
            setDescriptionValue("");
            templateStore.selectTemplate(undefined);
        }
    }, [store.newTaskDialogVisible, templateStore, form]);

    const loadProjectData = (projectId: string) => {
        const promise = Promise.all([
            store.loadUsersForProject(projectId),
            store.loadTaskTypesForProject(projectId),
            store.loadMetadata(projectId),
        ]);
        projectDataLoadRef.current = { projectId, promise };
        return promise;
    };

    const loadProjectDataForTemplate = (projectId: string) =>
        projectDataLoadRef.current?.projectId === projectId
            ? projectDataLoadRef.current.promise
            : loadProjectData(projectId);

    const setValuesFromTemplate = (template: TaskTemplate) => {
        const taskTypeId = getSelectableTaskTypeId(
            store.taskTypes,
            template.taskType,
        );

        form.setFieldsValue({
            projectId: template.projectId,
            taskType: taskTypeId,
            templateCode: template.templateCode,
            priority: template.defaultPriority ?? "LOW",
        });
        if (!templateStore.hasParameters) {
            form.setFieldsValue({
                name: template.title,
                description: template.description ?? "",
            });
            setDescriptionValue(template.description ?? "");
        }

        handleTaskTypeChange(taskTypeId);
    };

    React.useEffect(() => {
        const selectedTaskTemplateId = templateStore.selectedTaskTemplateId;
        const selectedTemplate = templateStore.selectedTemplate;

        if (!selectedTaskTemplateId || !selectedTemplate) {
            setIsTemplateApplied(false);
            return;
        }
        setIsTemplateApplied(true);
        setIsNameOverriden(false);

        form.setFieldsValue({
            templateCode: undefined,
            sharedWith: [],
            projectId: undefined,
            taskType: undefined,
            priority: "LOW",
        });

        const projectId = selectedTemplate.projectId || store.currentProjectId;

        if (projectId) {
            loadProjectDataForTemplate(projectId).then(() => {
                if (
                    templateStore.selectedTaskTemplateId ===
                    selectedTaskTemplateId
                ) {
                    setValuesFromTemplate(selectedTemplate);
                }
            });
        }
    }, [templateStore.selectedTaskTemplateId]);

    const handleSubmit = () => {
        form.validateFields()
            .then(async () => {
                const vals = form.getFieldsValue();
                const titleParams = vals.name.match(/\${[^}]+}/g) as
                    | string[]
                    | null;
                if (titleParams) {
                    for (const param of titleParams) {
                        const paramKey = param.match(/[^${}]+/g)![0];
                        const paramVal = vals[`title-${paramKey}`];
                        vals.name = (vals.name as string).replace(
                            param,
                            paramVal,
                        );
                        delete vals[paramKey];
                    }
                }
                const descriptionParams = vals.description?.match(
                    /\${[^}]+}/g,
                ) as string[] | null;
                if (descriptionParams) {
                    for (const param of descriptionParams) {
                        const paramKey = param.match(/[^${}]+/g)![0];
                        const paramVal = vals[`description-${paramKey}`];
                        vals.description = (vals.description as string).replace(
                            param,
                            paramVal,
                        );
                        delete vals[paramKey];
                    }
                }

                if (vals.metadata) {
                    for (const metadata of vals.metadata) {
                        const format = store.metadata.find(
                            (m) => m.name === metadata.metadataKey,
                        )!.format;
                        // Date in metadata represents absoulte value and is not bound to any timezone;
                        if (
                            dayjs(
                                metadata.metadataValue,
                                "YYYY-MM-DD",
                                true,
                            ).isValid()
                        ) {
                            if (format === ONLY_DATE) {
                                metadata.metadataValue =
                                    Utils.removeLocalTimeZone(
                                        metadata.metadataValue,
                                    );
                            } else if (format === FULL_DATE) {
                                metadata.metadataValue =
                                    Utils.ignoreLocalTimeZone(
                                        metadata.metadataValue,
                                    );
                            }
                        }
                    }
                }
                const task = await store.saveTask(vals);
                if (task) {
                    notifySuccess(task.id, task.alphaId);
                }
                store.setCurrentTaskType(null);
                store.setParamsSavedInUi(false);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const handleProjectChange = (projectId: string) => {
        form.setFieldsValue({
            assignedTo: undefined,
            sharedWith: [],
        });
        templateStore.setSelectedProjectId(projectId);
        loadProjectData(projectId).then(() => {
            form.setFieldsValue({ taskType: undefined });
        });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const metadataValidator = (rule: any, value: any, callback: any) => {
        if (!value) {
            callback();
        }

        if (
            Object.values(store.currentMetadataKey).filter(
                (m) => m.id === value,
            ).length > 1
        ) {
            callback("Duplicated keys are not allowed");
        } else {
            callback();
        }
    };
    const getDueDate = (dueDate: TaskTypeDueDate) => {
        const { period, count } = dueDate;
        switch (period) {
            case "D":
                return dayjs().add(count, "day");
            case "W":
                return dayjs().add(count, "week");
            case "M":
                return dayjs().add(count, "month");
            case "Y":
                return dayjs().add(count, "year");
            default:
                return dayjs();
        }
    };

    const getReminderDate = (dueDate: Dayjs, reminderDays: number) =>
        dueDate.subtract(reminderDays, "day");

    const disabledDate = (current: Dayjs) => {
        return current && current < dayjs().endOf("day");
    };

    const attachmentUploadProps: UploadProps = {
        multiple: true,
        accept: Utils.uploadAcceptExtensions,
        showUploadList: false,
        beforeUpload: (file) => {
            store.setTaskAttachments([...store.taskAttachments, file]);
            return false;
        },
        fileList: store.taskAttachments,
    };

    const removeAttachment = (file: RcFile) => {
        const index = store.taskAttachments.indexOf(file);
        const newFileList = store.taskAttachments.slice();
        newFileList.splice(index, 1);
        store.setTaskAttachments(newFileList);
    };

    const FormItemSkeleton = (
        <div style={{ marginBottom: -12 }}>
            <Skeleton
                title={false}
                avatar={false}
                paragraph={{ rows: 1, width: "100%" }}
                active
            />
        </div>
    );

    if (!store.projects || !store.projects.length || !store.currentProjectId) {
        return null;
    }

    const customTaskTypes = getSelectableTaskTypes(store.taskTypes);
    const shouldShowTaskType = customTaskTypes.length > 0;
    const shouldShowGeneralTaskType =
        store.taskTypes.length > 0 && !shouldShowTaskType;
    const shouldShowTypeField = shouldShowTaskType || shouldShowGeneralTaskType;
    const shouldReserveTypeField =
        shouldShowTypeField || store.taskTypesLoading;
    const generalTaskTypeTooltip =
        "This project uses the standard workflow. Admins can enable custom task types in Task Type settings.";

    const handleTaskTypeChange = (id?: string) => {
        if (!id) {
            store.setCurrentTaskType(null);
            form.setFieldsValue({
                assignedTo: undefined,
                dueDate: undefined,
                reminder: undefined,
            });
            return;
        }

        const taskType = store.taskTypes.find((t) => t.id === id);
        if (!taskType) {
            store.setCurrentTaskType(null);
            form.setFieldsValue({
                assignedTo: undefined,
                dueDate: undefined,
                reminder: undefined,
            });
            return;
        }

        store.setCurrentTaskType(id);
        form.setFieldsValue({ assignedTo: taskType.assignee ?? undefined });
        if (taskType.dueDate) {
            const dueDate = getDueDate(taskType.dueDate);
            form.setFieldsValue({
                dueDate,
                reminder: taskType.reminderDays
                    ? getReminderDate(dueDate, taskType.reminderDays)
                    : undefined,
            });
            return;
        }

        form.setFieldsValue({
            dueDate: undefined,
            reminder: undefined,
        });
    };

    const getUserLabel = (user: UserProfile) => {
        return (
            <>
                <span>{`${user?.firstName} ${user?.lastName}`}</span>
                <span style={{ color: "red" }}>
                    {user?.isDeleted ? " (Inactive)" : ""}
                </span>
            </>
        );
    };

    const renderTaskTypeLabel = (name: string, isSystemDefault?: boolean) => (
        <>
            {name}
            {isSystemDefault ? (
                <Tag className="alpha-info-tag" style={{ marginLeft: 8 }}>
                    System managed
                </Tag>
            ) : null}
        </>
    );

    const getWidget = (key: number) => {
        const metadata = store.currentMetadataKey[key];
        if (metadata?.isMultiValue) {
            return <MultiValueMetadataInput definition={metadata} />;
        }

        switch (metadata?.fieldType) {
            case "Text":
                return (metadata.listValues?.length ?? 0) === 0 ? (
                    <Input />
                ) : (
                    <Select allowClear options={metadata.listValues!} />
                );
            case "Boolean": {
                const { trueVal, falseVal } = metadata.format
                    ? JSON.parse(metadata.format)
                    : { trueVal: "true", falseVal: "false" };
                return (
                    <Select
                        allowClear
                        options={[
                            { label: trueVal || "true", value: "true" },
                            { label: falseVal || "false", value: "false" },
                        ]}
                    />
                );
            }
            case "DateTime":
                switch (metadata.format) {
                    case ONLY_DATE:
                    case FULL_DATE:
                        return (
                            <DatePicker
                                style={{ width: "100%" }}
                                suffixIcon={
                                    <i
                                        className="alpha-icon xs calendar-icon"
                                        style={{ margin: 0 }}
                                    />
                                }
                                format={Utils.getDateFormat(
                                    metadata.format === FULL_DATE,
                                )}
                                showTime={metadata.format === FULL_DATE}
                            />
                        );
                    default:
                        return <TimePicker style={{ width: "100%" }} />;
                }
            case "Number": {
                const { precision, separator } = metadata.format
                    ? JSON.parse(metadata.format)
                    : { precision: 2, separator: false };
                const step = `0.${"0".repeat((precision || 2) - 1)}1`;
                return (
                    <InputNumber
                        step={step}
                        formatter={
                            separator
                                ? (value) =>
                                      value!
                                          .toString()
                                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                : undefined
                        }
                        parser={(value) => value!.replace(/\$\s?|(,*)/g, "")}
                    />
                );
            }
            default:
                return <Input />;
        }
    };

    return (
        <div className="dialog-section">
            <Form
                form={form}
                onFinish={handleSubmit}
                className="inputs-container"
                id="new-task-modal-form"
                layout="vertical"
            >
                <FormItem name="templateCode" noStyle>
                    <Input type="hidden" />
                </FormItem>
                <Row gutter={24}>
                    <Col span={shouldReserveTypeField ? 12 : 24}>
                        <Observer>
                            {() => (
                                <FormItem
                                    colon={false}
                                    className="dialog-field"
                                    label={
                                        <span className="dialog-field-label">
                                            Project
                                        </span>
                                    }
                                    name="projectId"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Task project is required",
                                        },
                                    ]}
                                    initialValue={store.currentProjectId}
                                >
                                    <Select
                                        showSearch
                                        onChange={(val: string) => {
                                            handleProjectChange(val);
                                        }}
                                        options={
                                            store.projects &&
                                            store.projects.map((p) => ({
                                                label: p.name,
                                                value: p.id,
                                            }))
                                        }
                                        suffixIcon={
                                            <i
                                                className="alpha-icon xxs arrow-down-icon"
                                                style={{ margin: 0, width: 8 }}
                                            />
                                        }
                                        filterOption={Utils.filterOption}
                                        disabled={isTemplateApplied}
                                    />
                                </FormItem>
                            )}
                        </Observer>
                    </Col>
                    {store.taskTypesLoading && !shouldShowTypeField && (
                        <Col span={12}>
                            <FormItem
                                colon={false}
                                className="dialog-field"
                                label={
                                    <span className="dialog-field-label">
                                        Type
                                    </span>
                                }
                            >
                                {FormItemSkeleton}
                            </FormItem>
                        </Col>
                    )}
                    {shouldShowTaskType && (
                        <Col span={12}>
                            <Observer>
                                {() => (
                                    <FormItem
                                        name="taskType"
                                        colon={false}
                                        className="dialog-field"
                                        label={
                                            <span className="dialog-field-label">
                                                Type
                                            </span>
                                        }
                                    >
                                        <Select
                                            loading={store.taskTypesLoading}
                                            allowClear
                                            suffixIcon={
                                                <i
                                                    className="alpha-icon xxs arrow-down-icon"
                                                    style={{
                                                        margin: 0,
                                                        width: 8,
                                                    }}
                                                />
                                            }
                                            options={customTaskTypes.map(
                                                (p) => ({
                                                    label: renderTaskTypeLabel(
                                                        p.name,
                                                        p.isSystemDefault,
                                                    ),
                                                    value: p.id,
                                                    customFilterValue: p.name,
                                                }),
                                            )}
                                            filterOption={Utils.filterOption}
                                            showSearch
                                            placeholder="General"
                                            disabled={isTemplateApplied}
                                            onChange={handleTaskTypeChange}
                                        />
                                    </FormItem>
                                )}
                            </Observer>
                        </Col>
                    )}
                    {shouldShowGeneralTaskType && (
                        <Col span={12}>
                            <FormItem
                                colon={false}
                                className="dialog-field"
                                label={
                                    <span className="dialog-field-label">
                                        Type
                                        <Tooltip
                                            title={generalTaskTypeTooltip}
                                            placement="top"
                                        >
                                            <InfoCircleOutlined
                                                style={{ marginLeft: 6 }}
                                            />
                                        </Tooltip>
                                    </span>
                                }
                            >
                                <Input value="General" disabled />
                            </FormItem>
                        </Col>
                    )}
                </Row>
                <FormItem
                    name="name"
                    className="dialog-field"
                    label={<span className="dialog-field-label">Title</span>}
                    rules={[
                        { required: true, message: "Task title is required" },
                    ]}
                >
                    <Input
                        data-id="input-Name"
                        disabled={isTemplateApplied && !isNameOverriden}
                    />
                </FormItem>
                {isTemplateApplied &&
                    !isNameOverriden &&
                    !templateStore.templatTitleParameters?.length && (
                        <Button
                            className="override-title-button"
                            style={{ height: "1em" }}
                            size="small"
                            onClick={() => setIsNameOverriden(true)}
                            type="link"
                        >
                            Override task title
                        </Button>
                    )}
                <FormItem
                    name="description"
                    colon={false}
                    className="dialog-field"
                    label={
                        <span className="dialog-field-label">Description</span>
                    }
                >
                    {
                        <Editor
                            key={templateStore.selectedTaskTemplateId || "new"}
                            className={`${isTemplateApplied ? "disabled" : ""}`}
                            defaultEditMode="div"
                            tools={
                                isTemplateApplied ? undefined : AdvancedToolbar
                            }
                            contentStyle={
                                isTemplateApplied
                                    ? {
                                          height: 150,
                                          opacity: 0.4,
                                          pointerEvents: "none",
                                      }
                                    : { height: 150 }
                            }
                            value={descriptionValue}
                            onChange={(e) => {
                                if (!isTemplateApplied) {
                                    form.setFieldValue("description", e.html);
                                    setDescriptionValue(e.html);
                                }
                            }}
                        />
                    }
                </FormItem>
                <Row gutter={24}>
                    <Col span={12}>
                        <Observer>
                            {() => (
                                <FormItem
                                    name="assignedTo"
                                    colon={false}
                                    className="dialog-field"
                                    label={
                                        <span className="dialog-field-label">
                                            Assign to
                                        </span>
                                    }
                                >
                                    {!store.isUsersLoading ? (
                                        <Select
                                            placeholder="Unassigned"
                                            suffixIcon={
                                                <i
                                                    className="alpha-icon xxs arrow-down-icon"
                                                    style={{
                                                        margin: 0,
                                                        width: 8,
                                                    }}
                                                />
                                            }
                                            dropdownRender={(menu) => (
                                                <div className="assign-to-dropdown">
                                                    <div
                                                        className={
                                                            "limited-access-description" +
                                                            (isSharedWithVisible
                                                                ? " visible"
                                                                : "")
                                                        }
                                                    >
                                                        This task has a limited
                                                        access. You can assign
                                                        the task only to user
                                                        who has the access.
                                                    </div>
                                                    {menu}
                                                </div>
                                            )}
                                            options={store.filteredAssignToUsers.map(
                                                (p) => ({
                                                    label: getUserLabel(
                                                        p,
                                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                    ) as any,
                                                    value: p.userId,
                                                    customFilterValue: `${p.firstName} ${p.lastName}`,
                                                }),
                                            )}
                                            filterOption={
                                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                Utils.filterOption as any
                                            }
                                            showSearch
                                        />
                                    ) : (
                                        FormItemSkeleton
                                    )}
                                </FormItem>
                            )}
                        </Observer>
                    </Col>
                    <Col span={12}>
                        <FormItem
                            name="dueDate"
                            colon={false}
                            className="dialog-field"
                            label={
                                <span className="dialog-field-label">
                                    Due date
                                </span>
                            }
                        >
                            <DatePicker
                                suffixIcon={
                                    <i
                                        className="alpha-icon xs calendar-icon"
                                        style={{ margin: 0 }}
                                    />
                                }
                                style={{ width: "100%" }}
                                placeholder=""
                                disabledDate={disabledDate}
                                format={Utils.getDateFormat()}
                                showNow={false}
                            />
                        </FormItem>
                    </Col>
                    <Col span={12}>
                        <FormItem
                            name="priority"
                            initialValue="LOW"
                            colon={false}
                            className="dialog-field"
                            label={
                                <span className="dialog-field-label">
                                    Priority
                                </span>
                            }
                            rules={[
                                {
                                    required: true,
                                    message: "Task priority is required",
                                },
                            ]}
                        >
                            <Select
                                suffixIcon={
                                    <i
                                        className="alpha-icon xxs arrow-down-icon"
                                        style={{ margin: 0, width: 8 }}
                                    />
                                }
                                options={Utils.getTaskPrioritiesForDropdown()}
                            />
                        </FormItem>
                    </Col>
                    <Col span={12}>
                        <FormItem
                            name="reminder"
                            colon={false}
                            className="dialog-field"
                            label={
                                <span className="dialog-field-label">
                                    Reminder
                                </span>
                            }
                        >
                            <DatePicker
                                suffixIcon={
                                    <i
                                        className="alpha-icon xs calendar-icon"
                                        style={{ margin: 0 }}
                                    />
                                }
                                style={{ width: "100%" }}
                                placeholder=""
                                disabledDate={disabledDate}
                                format={Utils.getDateFormat()}
                                showNow={false}
                            />
                        </FormItem>
                    </Col>
                </Row>

                <Form.List name="metadata">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({ key, name }) => (
                                <Row gutter={24} key={key}>
                                    <Col span={12}>
                                        <FormItem
                                            name={[name, "metadataKey"]}
                                            colon={false}
                                            className="dialog-field"
                                            label={
                                                <span className="dialog-field-label">
                                                    Metadata key
                                                </span>
                                            }
                                            rules={[
                                                {
                                                    validator:
                                                        metadataValidator,
                                                },
                                                {
                                                    required: true,
                                                    message:
                                                        "Task metadata key is required",
                                                },
                                            ]}
                                        >
                                            <Select
                                                suffixIcon={
                                                    <i
                                                        className="alpha-icon xxs arrow-down-icon"
                                                        style={{
                                                            margin: 0,
                                                            width: 8,
                                                        }}
                                                    />
                                                }
                                                options={store.filteredMetadata.map(
                                                    (m) => ({
                                                        label: m.title,
                                                        value: m.name,
                                                    }),
                                                )}
                                                onChange={(val: string) =>
                                                    store.setCurrentMetadataKey(
                                                        key,
                                                        val,
                                                    )
                                                }
                                                showSearch
                                                filterOption={
                                                    Utils.filterOption
                                                }
                                                allowClear
                                            />
                                        </FormItem>
                                    </Col>
                                    <Col span={11} key={key}>
                                        <Observer>
                                            {() => (
                                                <FormItem
                                                    name={[
                                                        name,
                                                        "metadataValue",
                                                    ]}
                                                    colon={false}
                                                    className="dialog-field"
                                                    label={
                                                        <span className="dialog-field-label">
                                                            Metadata value
                                                        </span>
                                                    }
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message:
                                                                "Task metadata value is required",
                                                        },
                                                    ]}
                                                >
                                                    {getWidget(key)}
                                                </FormItem>
                                            )}
                                        </Observer>
                                    </Col>
                                    <div
                                        style={{
                                            margin: "auto",
                                            marginRight: 12,
                                        }}
                                    >
                                        <MinusCircleOutlined
                                            onClick={() => {
                                                remove(name);
                                                store.removeMetadataKey(name);
                                            }}
                                        />
                                    </div>
                                </Row>
                            ))}
                            <Form.Item style={{ marginTop: 5 }}>
                                <Button
                                    type="dashed"
                                    onClick={() => add()}
                                    block
                                    icon={<PlusOutlined />}
                                >
                                    Add metadata
                                </Button>
                            </Form.Item>
                        </>
                    )}
                </Form.List>
            </Form>
            <div className="attachments">
                <div className="attachments-container">
                    <span className="title">Attachments</span>
                    {store.hasFiles ? (
                        <>
                            <span className="items-amount">
                                &nbsp;({store.taskAttachments.length})
                            </span>
                            <span
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                }}
                                style={{ float: "right", marginRight: 10 }}
                            >
                                <Upload {...attachmentUploadProps}>
                                    <i className="alpha-icon xs add-attachment" />
                                </Upload>
                            </span>
                        </>
                    ) : null}
                </div>

                <div style={{ marginBottom: "12px" }}>
                    <Dragger
                        {...attachmentUploadProps}
                        openFileDialogOnClick={!store.hasFiles}
                    >
                        {store.hasFiles ? null : (
                            <span>
                                Drop files to attach or{" "}
                                <span
                                    style={{
                                        fontSize: "14px",
                                        color: "#273C75",
                                    }}
                                >
                                    browse
                                </span>
                            </span>
                        )}
                        {store.taskAttachments &&
                            store.taskAttachments.map((a) => (
                                <TaskCreateFormAttachment
                                    fileName={a.name || ""}
                                    fileSizeBytes={a.size}
                                    uploadDate={new Date().toISOString()}
                                    key={a.uid}
                                    onRemove={() => {
                                        removeAttachment(a);
                                    }}
                                />
                            ))}
                    </Dragger>
                </div>
            </div>
        </div>
    );
};

export default observer(TaskCreateForm);
